// Typed client for the candidate-journey endpoints (service/api_candidate.py).
// Everything rides the same /api/v1 client (cookie auth + CSRF) as Phase 1.
//
// Soft validation failures come back as HTTP 200 {ok:false,...} and flow through
// normally. Hard invite-invalidity (404/410/403) and scorecard-retry (409) throw
// ApiError; `tolerant()` flattens those into an `{ok:false}` shape so screens can
// uniformly branch on `.ok` instead of try/catch everywhere.

import { api, ApiError, ensureCsrf } from "./api";
import type {
  InviteData,
  OtpSendResult,
  OtpVerifyResult,
  SetupData,
  StartResult,
  PrepStatus,
  ScorecardData,
  SessionMeta,
  BriefData,
  HomeData,
  SettingsData,
  VadSpeed,
  ProfileData,
  ProfileUploadResult,
  KbProfileData,
} from "./types";

export interface ErrorResult {
  ok: false;
  error: string;
  status?: number;
  next?: string;
  field?: string;
  retry?: boolean;
}

async function tolerant<T>(p: Promise<T>): Promise<T | ErrorResult> {
  try {
    return await p;
  } catch (e) {
    if (e instanceof ApiError) {
      const body = (e.body ?? {}) as Record<string, unknown>;
      return {
        ok: false,
        error: (body.error as string) ?? e.message,
        status: e.status,
        next: body.next as string | undefined,
        field: body.field as string | undefined,
        retry: body.retry as boolean | undefined,
      };
    }
    return { ok: false, error: e instanceof Error ? e.message : "Network error" };
  }
}

const enc = encodeURIComponent;

export const candidateApi = {
  getInvite: (token: string) =>
    tolerant<InviteData>(api.get<InviteData>(`/invite/${enc(token)}`)),

  sendOtp: (token: string) =>
    api.post<OtpSendResult>(`/invite/${enc(token)}/otp/send`),

  verifyOtp: (token: string, code: string) =>
    api.post<OtpVerifyResult>(`/invite/${enc(token)}/otp/verify`, { code }),

  recordPrivacyConsent: (token: string, version: string) =>
    tolerant<{ ok: true; version: string; acceptedAt: string }>(
      api.post<{ ok: true; version: string; acceptedAt: string }>(`/invite/${enc(token)}/privacy-consent`, { version }),
    ),

  // JSON download, so not through api.get (which parses the body).
  exportData: async (token: string): Promise<{ ok: true; blob: Blob } | ErrorResult> => {
    try {
      const res = await fetch(`/api/v1/invite/${enc(token)}/export`, { credentials: "include" });
      if (res.ok) return { ok: true, blob: await res.blob() };
      const body = await res.json().catch(() => ({}));
      return { ok: false, status: res.status, error: body.error ?? "Could not download your data." };
    } catch {
      return { ok: false, error: "Could not download your data. Check your connection and try again." };
    }
  },

  authByToken: (token: string) =>
    tolerant<{ ok: true; next: string }>(
      api.post<{ ok: true; next: string }>(`/invite/${enc(token)}/auth/token`),
    ),

  getSetup: (token: string) =>
    tolerant<SetupData>(api.get<SetupData>(`/invite/${enc(token)}/setup`)),

  start: (token: string, form: FormData) =>
    tolerant<StartResult>(api.post<StartResult>(`/invite/${enc(token)}/start`, form)),

  sessionMeta: (id: string) =>
    tolerant<{ ok: true; session: SessionMeta }>(
      api.get<{ ok: true; session: SessionMeta }>(`/session/${enc(id)}`),
    ),

  prepStatus: (id: string) =>
    tolerant<PrepStatus>(api.get<PrepStatus>(`/session/${enc(id)}/prep-status`)),

  /** Returns {ok:true,status} or {ok:false, retry?:true, status} (409 = not yet finalized). */
  genScorecard: (id: string) =>
    tolerant<{ ok: true; status: string }>(
      api.post<{ ok: true; status: string }>(`/session/${enc(id)}/scorecard`),
    ),

  getScorecard: (id: string) =>
    tolerant<ScorecardData>(api.get<ScorecardData>(`/session/${enc(id)}/scorecard`)),

  getHomeData: (token: string) =>
    tolerant<HomeData>(api.get<HomeData>(`/invite/${enc(token)}/home-data`)),

  // Reads one role's brief. The token here is often NOT the invite the candidate
  // signed in on: the briefs list spans every role they were sent.
  getBrief: (token: string) =>
    tolerant<BriefData>(api.get<BriefData>(`/invite/${enc(token)}/brief`)),

  getSettings: (token: string) =>
    tolerant<SettingsData>(api.get<SettingsData>(`/invite/${enc(token)}/settings`)),

  saveSettings: (token: string, data: { displayName?: string; vadSpeed?: VadSpeed; notifyWeekly?: boolean; preferredVoice?: string }) =>
    tolerant<{ ok: true }>(api.post<{ ok: true }>(`/invite/${enc(token)}/settings`, data)),

  startEmailChange: (token: string, newEmail: string) =>
    tolerant<{ ok: boolean; error?: string; fallbackCode?: string }>(
      api.post<{ ok: boolean; error?: string; fallbackCode?: string }>(`/invite/${enc(token)}/settings/email-change/start`, { new_email: newEmail }),
    ),

  verifyEmailChange: (token: string, newEmail: string, code: string) =>
    tolerant<{ ok: boolean; error?: string; email?: string }>(
      api.post<{ ok: boolean; error?: string; email?: string }>(`/invite/${enc(token)}/settings/email-change/verify`, { new_email: newEmail, code }),
    ),

  getProfile: (token: string) =>
    tolerant<ProfileData>(api.get<ProfileData>(`/invite/${enc(token)}/profile`)),

  updateProfile: (token: string, form: FormData) =>
    tolerant<ProfileUploadResult>(api.post<ProfileUploadResult>(`/invite/${enc(token)}/profile`, form)),

  getKbProfile: (token: string) =>
    tolerant<KbProfileData>(api.get<KbProfileData>(`/invite/${enc(token)}/kb`)),

  notificationOptin: (token: string, interviewDate: string | null, granted = false) =>
    tolerant<{ ok: true }>(
      api.post<{ ok: true }>(`/invite/${enc(token)}/notification-optin`, {
        interview_date: interviewDate,
        granted,
      }),
    ),

  ttsSpeak: (token: string, text: string): Promise<{ ok: boolean; url?: string }> =>
    api.post<{ ok: boolean; url?: string }>(`/invite/${enc(token)}/tts/speak`, { text }),

  // Scorecard's spoken summary — pre-generated in the background, this just
  // returns the URL (instant if ready, generates on demand as a fallback).
  scorecardNarration: (sessionId: string): Promise<{ ok: boolean; url?: string; error?: string }> =>
    api.post<{ ok: boolean; url?: string; error?: string }>(`/session/${enc(sessionId)}/narration`),

  async *chatMessage(token: string, message: string): AsyncGenerator<string> {
    const csrf = await ensureCsrf();
    const headers: Record<string, string> = { "Content-Type": "application/json", "X-CSRFToken": csrf };
    const r = await fetch(`/api/v1/invite/${enc(token)}/chat`, {
      method: "POST",
      headers,
      body: JSON.stringify({ message }),
      credentials: "include",
    });
    if (!r.body) return;
    const reader = r.body.getReader();
    const decoder = new TextDecoder();
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      const lines = decoder.decode(value).split("\n");
      for (const line of lines) {
        if (line === "data: [DONE]") return;
        if (line.startsWith("data: ")) {
          try {
            const parsed = JSON.parse(line.slice(6)) as { t?: string; error?: string };
            if (parsed.error) throw new Error(parsed.error);
            if (parsed.t) yield parsed.t;
          } catch (e) {
            if (e instanceof SyntaxError) continue; // malformed chunk — skip
            throw e;
          }
        }
      }
    }
  },
};
